import {
  PDF_CONFIG,
  PT_TO_MM,
  TWO_COLUMN_COL_GAP_RATIO,
  TWO_COLUMN_DIVIDER_TO_ROW_MM,
  TWO_COLUMN_LEFT_COL_RATIO,
  TWO_COLUMN_RIGHT_COL_RATIO,
} from './resumeExportPdfConfig'

/** A4 layout shared with jsPDF export (resumeExport.ts / resumeExportTwoColumn.ts). */
export const RESUME_PREVIEW_PAGE = {
  pageWidthMm: PDF_CONFIG.pageWidth,
  pageHeightMm: PDF_CONFIG.pageHeight,
  marginTopMm: PDF_CONFIG.marginPt.top * PT_TO_MM,
  marginBottomMm: PDF_CONFIG.marginPt.bottom * PT_TO_MM,
  marginLeftMm: PDF_CONFIG.marginPt.left * PT_TO_MM,
  marginRightMm: PDF_CONFIG.marginPt.right * PT_TO_MM,
} as const

export function resumePreviewContentHeightMm(): number {
  return (
    RESUME_PREVIEW_PAGE.pageHeightMm -
    RESUME_PREVIEW_PAGE.marginTopMm -
    RESUME_PREVIEW_PAGE.marginBottomMm
  )
}

export function resumePreviewContentWidthMm(): number {
  return (
    RESUME_PREVIEW_PAGE.pageWidthMm -
    RESUME_PREVIEW_PAGE.marginLeftMm -
    RESUME_PREVIEW_PAGE.marginRightMm
  )
}

/** Column widths inside printable band — same formula as jsPDF two-column export. */
export function twoColumnColumnWidthsMm(
  contentWidthMm = resumePreviewContentWidthMm()
): { leftMm: number; gapMm: number; rightMm: number } {
  const gapMm = contentWidthMm * TWO_COLUMN_COL_GAP_RATIO
  const leftMm = contentWidthMm * TWO_COLUMN_LEFT_COL_RATIO
  const rightMm = contentWidthMm * TWO_COLUMN_RIGHT_COL_RATIO
  return { leftMm, gapMm, rightMm }
}

/** Measure how many CSS pixels equal 1 mm in the current browser (for mm-based layout). */
export function measureMmToPx(): number {
  if (typeof document === 'undefined') return 96 / 25.4
  const el = document.createElement('div')
  el.style.cssText = 'position:absolute;visibility:hidden;width:100mm;height:0;pointer-events:none'
  document.body.appendChild(el)
  const px = el.offsetWidth / 100
  document.body.removeChild(el)
  return px > 0 ? px : 96 / 25.4
}

export function mmToPx(mm: number, mmToPx = measureMmToPx()): number {
  return mm * mmToPx
}

/** Gap from contact divider to target title / SKILLS in two-column preview (PDF `TWO_COLUMN_DIVIDER_TO_ROW_MM`). */
export function twoColumnPreviewDividerToContentGapPx(mmRatio = measureMmToPx()): number {
  return mmToPx(TWO_COLUMN_DIVIDER_TO_ROW_MM, mmRatio)
}

/** Page count when slicing by full sheet height (legacy). */
export function resumePreviewPageCount(contentHeightPx: number, pageHeightPx: number): number {
  if (pageHeightPx <= 0) return 1
  return Math.max(1, Math.ceil(contentHeightPx / pageHeightPx))
}

/** Page count from body height and printable band height (between top/bottom margins). */
export function resumePreviewPageCountForBody(
  bodyHeightPx: number,
  contentZoneHeightPx: number
): number {
  if (contentZoneHeightPx <= 0) return 1
  return Math.max(1, Math.ceil(bodyHeightPx / contentZoneHeightPx))
}

export type ResumePreviewPageMetrics = {
  pageWidthPx: number
  pageHeightPx: number
  marginTopPx: number
  marginBottomPx: number
  marginLeftPx: number
  marginRightPx: number
  contentWidthPx: number
  contentZoneHeightPx: number
}

/** Measured table column positions for two-column preview clipping (DOM, not 62%+gap formula). */
export type TwoColumnPreviewLayout = {
  /** Shared top of the column row (use for both columns’ vertical scroll). */
  columnsRowTopPx: number
  /** @deprecated use columnsRowTopPx */
  columnsTopPx: number
  leftTopPx: number
  rightTopPx: number
  leftShiftPx: number
  rightShiftPx: number
  leftWidthPx: number
  gapWidthPx: number
  rightWidthPx: number
}

/** Walk offsetParent chain — layout px, unaffected by CSS transform scale on ancestors. */
function layoutOffsetWithinRoot(el: HTMLElement, root: HTMLElement): { top: number; left: number } {
  let top = 0
  let left = 0
  let node: HTMLElement | null = el
  while (node && node !== root) {
    top += node.offsetTop
    left += node.offsetLeft
    node = node.offsetParent instanceof HTMLElement ? node.offsetParent : null
    if (node && node !== root && !root.contains(node)) break
  }
  return { top, left }
}

export function measureTwoColumnPreviewLayout(
  root: HTMLElement,
  leftEl: HTMLElement | null,
  rightEl: HTMLElement | null
): TwoColumnPreviewLayout {
  let columnsRowTopPx = 0
  let leftTopPx = 0
  let rightTopPx = 0
  let leftShiftPx = 0
  let rightShiftPx = 0
  let leftWidthPx = 0
  let gapWidthPx = 0
  let rightWidthPx = 0

  const gapEl = root.querySelector<HTMLElement>('.paysafe-col-gap')

  const rowEl =
    root.querySelector<HTMLElement>('.paysafe-columns tbody tr') ??
    leftEl?.closest('tr') ??
    rightEl?.closest('tr') ??
    null
  if (rowEl) {
    columnsRowTopPx = Math.round(layoutOffsetWithinRoot(rowEl, root).top)
  }

  if (leftEl) {
    const { top, left } = layoutOffsetWithinRoot(leftEl, root)
    leftTopPx = Math.round(top)
    if (!columnsRowTopPx) columnsRowTopPx = leftTopPx
    leftShiftPx = Math.round(left)
    leftWidthPx = Math.round(leftEl.offsetWidth)
  }
  if (gapEl) {
    gapWidthPx = Math.round(gapEl.offsetWidth)
  }

  if (rightEl) {
    const { top, left } = layoutOffsetWithinRoot(rightEl, root)
    rightTopPx = Math.round(top)
    if (!columnsRowTopPx) columnsRowTopPx = rightTopPx
    rightShiftPx = Math.round(left)
    rightWidthPx = Math.round(rightEl.offsetWidth)
  }

  if (leftTopPx && rightTopPx) {
    columnsRowTopPx = Math.min(leftTopPx, rightTopPx)
  }

  if (!gapWidthPx && leftWidthPx > 0 && rightShiftPx > leftShiftPx + leftWidthPx) {
    gapWidthPx = rightShiftPx - leftShiftPx - leftWidthPx
  }

  return {
    columnsRowTopPx,
    columnsTopPx: columnsRowTopPx,
    leftTopPx,
    rightTopPx,
    leftShiftPx,
    rightShiftPx,
    leftWidthPx,
    gapWidthPx,
    rightWidthPx,
  }
}

/**
 * Top band on sheet 1: header through column row top (divider→content gap is padding inside column cells).
 * Column viewports must not start flush under the divider — gap lives in column padding-top.
 */
export function twoColumnSheet1ReservedTopPx(
  headerHeightPx: number,
  layout: Pick<TwoColumnPreviewLayout, 'columnsRowTopPx'>
): number {
  if (headerHeightPx <= 0) return 0
  return Math.max(headerHeightPx, layout.columnsRowTopPx)
}

export function computeResumePreviewPageMetrics(): ResumePreviewPageMetrics {
  const mmToPxRatio = measureMmToPx()
  const pageWidthPx = mmToPx(RESUME_PREVIEW_PAGE.pageWidthMm, mmToPxRatio)
  const pageHeightPx = mmToPx(RESUME_PREVIEW_PAGE.pageHeightMm, mmToPxRatio)
  const marginTopPx = mmToPx(RESUME_PREVIEW_PAGE.marginTopMm, mmToPxRatio)
  const marginBottomPx = mmToPx(RESUME_PREVIEW_PAGE.marginBottomMm, mmToPxRatio)
  const marginLeftPx = mmToPx(RESUME_PREVIEW_PAGE.marginLeftMm, mmToPxRatio)
  const marginRightPx = mmToPx(RESUME_PREVIEW_PAGE.marginRightMm, mmToPxRatio)
  const contentWidthPx = Math.max(0, pageWidthPx - marginLeftPx - marginRightPx)
  const contentZoneHeightPx = Math.max(0, pageHeightPx - marginTopPx - marginBottomPx)
  return {
    pageWidthPx,
    pageHeightPx,
    marginTopPx,
    marginBottomPx,
    marginLeftPx,
    marginRightPx,
    contentWidthPx,
    contentZoneHeightPx,
  }
}
